import React, { useState, useEffect, useContext, useRef } from "react";
import ReactPaginate from "react-paginate";
import { format } from "date-fns";
import { v4 as uuidv4 } from "uuid";
import {
  MdOutlineArrowBackIos,
  MdOutlineArrowForwardIos,
} from "react-icons/md";
import { RiFilter2Fill } from "react-icons/ri";
import { AiOutlineSearch } from "react-icons/ai";
import { AiOutlineCloseCircle } from "react-icons/ai";
import { IoIosArrowDropleftCircle, IoIosArrowDroprightCircle } from "react-icons/io";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Form811Data from "../api/eight_one_one_form";
import { AuthContext } from "../context/Auth/AuthContext";
import Navbar from "../components/common/Navbar";
import Table from "../components/eight_one_one_form-table/Table";
import PdfViewer from "../components/pdf-viewer/PdfViewer";
import Loading from "../components/material-ui/Loading";
import ButtonUI from "../components/material-ui/ButtonUI";
import BasicDatePicker from "../components/material-ui/DatePicker";
import baseUrl from "../config/baseUrl";
import "../styles/DocumentTable.css";

const Form811 = () => {
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const [openFilter, setOpenFilter] = useState(false);
  const [docsCount, setDocsCount] = useState(0);
  const [documentResponse, setDocumentResponse] = useState([]);
  const [documentDeleted, setDocumentDeleted] = useState(0);

  const [fileTypeOptions, setFileTypeOptions] = useState([]);
  const [userList, setUserList] = useState([]);

  const [globalSearch, setGlobalSearch] = useState("");
  const [searchedCaseNumber, setSearchedCaseNumber] = useState("");
  const [selectedFileTypeOptions, setSelectedFileTypeOptions] = useState(null);
  const [modifiedBy, setModifiedBy] = useState(null);
  const [searchedDate, setSearchedDate] = useState(null);
  const [modifiedDate, setModifiedDate] = useState(null);

  // ── Tab state ──────────────────────────────────────────────────────────────
  const [tab, setTab] = useState([
    {
      id: uuidv4(),
      documentID: null,
      title: "8821 Form",
      canClose: false,
    },
  ]);
  const [tabValue, setTabValue] = useState(() => tab[0].id);
  const scrollRef = useRef(null);
  // ──────────────────────────────────────────────────────────────────────────

  const { token, userID, isAdmin } = useContext(AuthContext);

  const limit = 20;

  const [api, setApi] = useState(
    `${baseUrl}/apireact/form8821documents?limit=20&offset=0&isAdmin=${isAdmin}`
  );

  const setDocumentDeletedToTrue = () =>{
     setDocumentDeleted((count) => count + 1);
  }

  // ── Tab helpers ────────────────────────────────────────────────────────────
  const truncate = (string, n) =>
    string?.length > n ? string.substr(0, n - 1) + "..." : string;

  const changeActiveTab = () => {
    setTabValue(tab[0].id);
  };

  const addDocumentTab = (document) => {
    const isDocumentAlreadySelected = tab.find(
      (t) => t.documentID === document.documentID
    );
    if (isDocumentAlreadySelected !== undefined) {
      // Tab already open — just switch to it
      setTabValue(isDocumentAlreadySelected.id);
      return;
    }

    const newTab = {
      id: uuidv4(),
      documentID: document.documentID,
      title: truncate(document.title, 9),
      content: () => (
        <PdfViewer
          documentType={document.extension}
          caseNumber={document.case_number}
          documentID={document.documentID}
          documentTitle={document.title}
          changeActiveTab={changeActiveTab}
        />
      ),
      canClose: true,
    };

    setTab((prevTabs) => [...prevTabs, newTab]);
    setTabValue(newTab.id);
  };

  const closeTab = (e, id) => {
    e.stopPropagation();
    setTab((prevTabs) => {
      const newTabs = prevTabs.filter((t) => t.id !== id);
      setTabValue(newTabs[0].id);
      return newTabs;
    });
  };

  const scrollLeft = (offset) => {
    scrollRef.current.scrollLeft += offset;
  };

  const scrollRight = (offset) => {
    scrollRef.current.scrollLeft -= offset;
  };
  // ──────────────────────────────────────────────────────────────────────────

  const buildQueryString = ({
    search = globalSearch,
    fileType = selectedFileTypeOptions,
    caseNum = searchedCaseNumber,
    dateEntered = searchedDate,
    dateMod = modifiedDate,
    userFilter = modifiedBy,
  } = {}) => {
    let qs = "";
    if (search)      qs += `&global_search=${search}`;
    if (fileType)    qs += `&document_type=${fileType}`;
    if (caseNum)     qs += `&case_number=${caseNum}`;
    if (dateEntered) qs += `&created_at=${dateEntered.toLocaleDateString("fr-CA")}`;
    if (dateMod)     qs += `&date_modified=${dateMod.toLocaleDateString("fr-CA")}`;
    if (userFilter)  qs += `&userIdFilter=${userFilter.id}`;
    return qs;
  };

  const fetchFilterOptions = async () => {
    try {
      const response = await Form811Data({
        token,
        api: `${baseUrl}/apireact/form8821documents?limit=1000&offset=0&isAdmin=${isAdmin}`,
      });
      const allData = response?.data || [];

      const uniqueFileTypes = [
        ...new Set(
          allData
            .map((item) => item.extension)
            .filter((ext) => ext !== null && ext !== "")
        ),
      ];
      setFileTypeOptions(uniqueFileTypes);

      const userMap = new Map();
      allData.forEach((item) => {
        if (item.modified_user_name && !userMap.has(item.modified_user_name)) {
          userMap.set(item.modified_user_name, {
            id: item.modified_user_name,
            name: item.modified_user_name,
          });
        }
      });
      setUserList([...userMap.values()]);
    } catch (err) {
      console.error("Failed to fetch filter options:", err);
    }
  };

  const documentResponseFunc = async () => {
    setLoading(true);
    try {
      const response = await Form811Data({ token, api });
      const data = response?.data || [];

      setDocsCount(response?.DocsCount || 0);
      setPageCount(Math.ceil((response?.DocsCount || 0) / limit));
      if (currentPage > Math.ceil((response?.DocsCount || 0) / limit)) {
        setCurrentPage(Math.ceil((response?.DocsCount || 0) / limit));
      }
      setDocumentResponse(data);
    } catch (err) {
      console.error("Failed to fetch documents:", err);
      setDocumentResponse([]);
    }
    setLoading(false);
  };

  useEffect(() => { fetchFilterOptions(); }, []);
  useEffect(() => { documentResponseFunc(); }, [api]);

  useEffect(() => {
    documentResponseFunc();
  },[api, documentDeleted]);

  useEffect(() => {
    const offset = currentPage * limit;
    setApi(
      `${baseUrl}/apireact/form8821documents?limit=${limit}&offset=${offset}&isAdmin=${isAdmin}${buildQueryString()}`
    );
  }, [currentPage]);

  useEffect(() => {
    const debounceFn = setTimeout(() => {
      setCurrentPage(0);
      setApi(
        `${baseUrl}/apireact/form8821documents?limit=20&offset=0&isAdmin=${isAdmin}${buildQueryString({ search: globalSearch })}`
      );
    }, 2500);
    return () => clearTimeout(debounceFn);
  }, [globalSearch]);

  const getAllDataFunc = async () => {
    const response = await Form811Data({
      token,
      api: `${baseUrl}/apireact/form8821documents?limit=${docsCount}&offset=0&isAdmin=${isAdmin}${buildQueryString()}`,
    });
    const data = response?.data || [];
    return data.map((d) => ({
      title: d.title || "N/A",
      extension: d.extension || "N/A",
      case_number: d.case_number !== "" ? d.case_number : "N/A",
      date_entered:
        d.date_entered &&
        d.date_entered !== "0000-00-00" &&
        d.date_entered !== "0000-00-00 00:00:00"
          ? format(new Date(d.date_entered), "MM-dd-yyyy HH:mm")
          : "N/A",
      date_modified:
        d.date_modified &&
        d.date_modified !== "0000-00-00" &&
        d.date_modified !== "0000-00-00 00:00:00"
          ? format(new Date(d.date_modified), "MM-dd-yyyy HH:mm")
          : "N/A",
      poa_status: d.poa_status || "N/A",
      modified_user_name: d.modified_user_name ?? "N/A",
    }));
  };

  const onGlobalSearchChange = (e) => setGlobalSearch(e.target.value);
  const onSearchedCaseNumberChange = (e) => setSearchedCaseNumber(e.target.value);
  const onFileTypeOptionChange = (selectedFileType) => setSelectedFileTypeOptions(selectedFileType);
  const onModifiedByChange = (selectedUser) => setModifiedBy(selectedUser);
  const onSearchedDateChange = (newDate) => setSearchedDate(newDate);
  const onModifiedDateChange = (newDate) => setModifiedDate(newDate);
  const filterVisibility = () => setOpenFilter(!openFilter);

  const applyFilters = () => {
    setCurrentPage(0);
    setApi(
      `${baseUrl}/apireact/form8821documents?limit=${limit}&offset=0&isAdmin=${isAdmin}${buildQueryString({
        search: globalSearch,
        fileType: selectedFileTypeOptions,
        caseNum: searchedCaseNumber,
        dateEntered: searchedDate,
        dateMod: modifiedDate,
        userFilter: modifiedBy,
      })}`
    );
  };

  const clearFilters = () => {
    setGlobalSearch("");
    setSearchedCaseNumber("");
    setSelectedFileTypeOptions(null);
    setModifiedBy(null);
    setSearchedDate(null);
    setModifiedDate(null);
    setCurrentPage(0);
    setApi(
      `${baseUrl}/apireact/form8821documents?limit=${limit}&offset=0&isAdmin=${isAdmin}`
    );
  };

  const handlePageClick = ({ selected: selectedPage }) => {
    setCurrentPage(selectedPage);
  };

  // ── Table UI (extracted so it can live inside a tab) ───────────────────────
  const tableUI = (
    <div className="documents-content">
      <div className="table-header">
        <TextField
          name="globalSearch"
          label="Search"
          type="text"
          autoComplete="off"
          value={globalSearch}
          onChange={onGlobalSearchChange}
          variant="outlined"
          InputProps={{ endAdornment: <AiOutlineSearch /> }}
        />
        <div className="doc-header-btn">
          <button className="filter-documents" onClick={filterVisibility}>
            <RiFilter2Fill />
          </button>
        </div>
      </div>

      <div className={openFilter ? "document-filter" : "hide-document-filter"}>
        <BasicDatePicker value={searchedDate} setValue={onSearchedDateChange} label="Created_at" />
        <BasicDatePicker value={modifiedDate} setValue={onModifiedDateChange} label="Date Modified" />
        <Autocomplete
          id="select-file-type-filter"
          options={fileTypeOptions}
          sx={{ width: 154 }}
          renderInput={(params) => <TextField {...params} label="File Type" />}
          value={selectedFileTypeOptions}
          onChange={(e, selectedOption) => onFileTypeOptionChange(selectedOption)}
        />
        <Autocomplete
          id="select-modified-by-filter"
          options={userList.map((user) => ({ ...user, label: user.name }))}
          sx={{ width: 154 }}
          renderInput={(params) => <TextField {...params} label="Modified By" />}
          value={modifiedBy}
          onChange={(e, selectedOption) => onModifiedByChange(selectedOption)}
          isOptionEqualToValue={(option, value) => option.id === value?.id}
        />
        <TextField
          name="caseID"
          label="Case ID"
          type="text"
          autoComplete="off"
          value={searchedCaseNumber}
          onChange={onSearchedCaseNumberChange}
          variant="outlined"
        />
        <ButtonUI
          formButton={false}
          variant="contained"
          buttonText="Apply"
          style={{ width: "80px", height: "35px", color: "#007bff", backgroundColor: "white", boxShadow: "none", border: "1px solid #007bff", fontWeight: "500", borderRadius: "4px", letterSpacing: "0.07rem" }}
          onClickFunc={applyFilters}
        />
        <ButtonUI
          formButton={false}
          variant="contained"
          buttonText="Reset"
          style={{ width: "80px", height: "35px", color: "#007bff", backgroundColor: "white", boxShadow: "none", border: "1px solid #007bff", fontWeight: "500", borderRadius: "4px", letterSpacing: "0.07rem" }}
          onClickFunc={clearFilters}
        />
      </div>

      {!loading ? (
        documentResponse.length > 0 ? (
          <div className="document__table">
            <Table
              loading={loading}
              openFilter={openFilter}
              documentTableData={documentResponse}
              getAllDataFunc={getAllDataFunc}
              docsCount={docsCount}
              addDocumentTab={addDocumentTab} 
              setDocumentDeletedToTrue={setDocumentDeletedToTrue}
              isAdmin={isAdmin}
            />
            <div className="table-pagination">
              <ReactPaginate
                previousLabel={<MdOutlineArrowBackIos />}
                nextLabel={<MdOutlineArrowForwardIos />}
                pageCount={pageCount}
                onPageChange={handlePageClick}
                forcePage={currentPage}
                containerClassName={"doc-pagination"}
                previousLinkClassName={"doc-pagination-link"}
                nextLinkClassName={"doc-pagination-link"}
                disabledClassName={"doc-pagination-link-disabled"}
                activeClassName={"doc-pagination-link-active"}
              />
            </div>
          </div>
        ) : (
          <h5 className="no-doc-data">No Data Available</h5>
        )
      ) : (
        <Loading />
      )}
    </div>
  );

  return (
    <div className="document-container">
      <div className="navbar">
        <Navbar />
      </div>
      <div className="document-content">

        {/* ── Tab Bar ── */}
        <div className={tab.length > 7 ? "scrollable-tabs" : "not-scrollable-tabs"}>
          {tab.length > 7 && (
            <div className="scroll-icons" onClick={() => scrollRight(150)}>
              <IoIosArrowDropleftCircle />
            </div>
          )}
          <div className="tabs-header" ref={scrollRef}>
            {tab.map((t) => (
              <div
                key={t.id}
                className={tabValue === t.id ? "tab-title active-tab-title" : "tab-title"}
                onClick={() => setTabValue(t.id)}
              >
                <span>{t.title}</span>
                {t.canClose && (
                  <button className="tab-close-icon" onClick={(e) => closeTab(e, t.id)}>
                    <AiOutlineCloseCircle />
                  </button>
                )}
              </div>
            ))}
          </div>
          {tab.length > 7 && (
            <div className="scroll-icons" onClick={() => scrollLeft(150)}>
              <IoIosArrowDroprightCircle />
            </div>
          )}
        </div>

        {/* ── Tab Content ── */}
        <div className="tab-content">
          {tab.map((t) =>
            tabValue === t.id ? (
              <div key={t.id}>
                {t.title === "8821 Form" ? tableUI : t.content()}
              </div>
            ) : null
          )}
        </div>

      </div>
    </div>
  );
};

export default Form811;
